import React, { useContext, useEffect, useState } from "react";
import { AUTH_SERVICE } from "../services/auth-service";
import { removeAuthData } from "../utils/auth";
import { eventEmitter } from '../services/config';

interface User {
  id: number;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
}

interface PersistUserResponse {
  success: boolean;
  status: number;
  data: User;
}

interface AuthContextProps {
  isAuth: boolean;
  type: string;
  setAppType: (appType: string) => void;
  user: User | null;
  token: string | null;
  userRole: string;
  permissions: string[];
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setUserRole: (role: string) => void;
  setPermissions: (permissions: string[]) => void;
  setIsAuth: (auth: boolean) => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextProps>({
  isAuth: false,
  type: "hff",
  setAppType: () => { },
  user: null,
  token: null,
  userRole: "",
  permissions: [],
  setUser: () => { },
  setToken: () => { },
  setUserRole: () => { },
  setPermissions: () => { },
  setIsAuth: () => { },
  logout: () => { }
});

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem("token");
  });

  const [userRole, setUserRole] = useState<string>(() => {
    return localStorage.getItem("userRole") || "";
  });

  const [type, setType] = useState<string>(() => {
    return localStorage.getItem("type") || "hff";
  });

  const setAppType = (appType: string) => {
    localStorage.setItem("type", appType);
    setType(appType);
  }

  const [permissions, setPermissions] = useState<string[]>(() => {
    const storedPermissions = localStorage.getItem("permissions");
    return storedPermissions ? JSON.parse(storedPermissions) : [];
  });



  const [isAuth, setIsAuth] = useState(() => {
    try {
      const storedAuth = localStorage.getItem("isAuth");
      return storedAuth ? JSON.parse(storedAuth) : false;
    } catch (error) {
      console.error("Failed to parse isAuth data:", error);
      return false;
    }
  });

  const logout = () => {
    setIsAuth(false);
    setUser(null);
    setToken(null);
    setUserRole("");
    setPermissions([]);
    setType("hff");
    removeAuthData();
  };


  useEffect(() => {
    const persistUser = async () => {
      try {
        const response = await AUTH_SERVICE.persistUser("/api/crm/me") as PersistUserResponse;
        if (response?.success) {
          const userData = response.data;
          const role = userData.roles[0];

          localStorage.setItem("isAuth", "true");
          localStorage.setItem("user", JSON.stringify(userData));
          localStorage.setItem("userRole", role);
          localStorage.setItem("permissions", JSON.stringify(userData.permissions));
          localStorage.setItem("type", "hff");

          setIsAuth(true);
          setUser(userData);
          setUserRole(role);
          setPermissions(userData.permissions);
        }
      } catch (error) {
        removeAuthData();
        console.error("Error fetching user:", error);
      }
    };

    persistUser();
  }, [token]);

  useEffect(() => {
    const handleLogout = () => {
      logout();
    };
    eventEmitter.addEventListener('logout', handleLogout);
    return () => {
      eventEmitter.removeEventListener('logout', handleLogout);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        type,
        setAppType,
        user,
        token,
        userRole,
        permissions,
        setUser,
        setToken,
        setUserRole,
        setPermissions,
        setIsAuth,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const AuthConsumer = AuthContext.Consumer;

export { AuthProvider, AuthConsumer, AuthContext };
export const useAuth = () => useContext(AuthContext);
